import React from 'react';
import {
  VStack, HStack, Center, Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { isMeetingOnlyTask } from '@/common/utils/tasks/isEventDeliveredTask';
import { isOralExamTask } from '@/common/utils/tasks/isOralExamTask';
import ScrollableScreenContainer from '../../ScrollableScreenContainer';
import { usePreviewContent, usePreviewNavigation } from '../hooks/useModulePreviewStore';
import useModulePreviewData from '../hooks/useModulePreviewData';

// Import student components
import CurrentModuleCard from '../../../../student/module/components/CurrentModuleCard';
import TaskList from '../../../../student/module/components/TaskList';
import TaskPreviewSelector from '../../../../student/module/components/TaskPreviewSelector';
import TaskSidebarSelector from '../../../../student/module/components/TaskSidebarSelector';

import ModulePreviewNavigation from './ModulePreviewNavigation';

/**
 * ModulePreview component
 * Adapts the student ModuleScreen for use in preview mode with left-side navigation
 */
const ModulePreview = () => {
  const { t } = useTranslation();
  const { module: rawModule, tasks: rawTasks, selectedTask: rawSelectedTask } = usePreviewContent();
  const { selectTask, setActiveView } = usePreviewNavigation();

  // Transform data for student components
  const previewData = useModulePreviewData(rawModule, rawTasks);
  const { module, tasks } = previewData;
  const selectedTask = tasks?.find((task) => task.id === rawSelectedTask?.id);

  // Stable preview scope to satisfy hooks expecting a course/module context
  const selectedModulePreview = { moduleId: module?.id, courseId: 'preview' };

  if (!module || !tasks || tasks.length === 0) {
    return (
      <HStack flex={1}>
        <ModulePreviewNavigation />
        <Center flex={1}>
          <Text>No module data available for preview</Text>
        </Center>
      </HStack>
    );
  }

  // Mock handlers for student components
  const handleSelectTask = (task) => {
    if (task && task.id) {
      selectTask(task.id);
    }
  };

  const handleOpenTask = () => {
    if (!selectedTask) {
      return;
    }
    if (isOralExamTask(selectedTask)) {
      setActiveView('task');
      return;
    }
    // Other meeting-only tasks stay on the module view.
    if (!isMeetingOnlyTask(selectedTask)) {
      setActiveView('task');
    }
  };

  // All tasks are completed in preview mode
  const allTasksCompleted = false;

  return (
    <ScrollableScreenContainer>
      <VStack space="lg" mb="$8">
        <CurrentModuleCard
          module={module}
          moduleImage={module.imageUrl}
          // course={module.courses[0]}
          $base-display="none"
          $md-display="flex"
        />

        <HStack space="lg">
          <TaskList
            $md-w={250}
            $lg-w={300}
            module={module}
            tasks={tasks}
            currentTask={selectedTask}
            handleSelectTask={handleSelectTask}
            selectedModule={selectedModulePreview}
            readOnly
          />

          <VStack flex={1} space="lg" $base-display="none" $sm-display="flex">
            {selectedTask && (
              <TaskPreviewSelector
                task={selectedTask}
                loading={false}
                module={module}
                tasks={tasks}
                selectedModule={selectedModulePreview}
                handleOpenTask={handleOpenTask}
                readOnly
              />
            )}
            {!selectedTask && !allTasksCompleted ? (
              <Center flex={1}>
                <Text size="sm">{t('student.module.messages.no-task-selected')}</Text>
              </Center>
            ) : null}
          </VStack>

          <TaskSidebarSelector
            w={200}
            $base-display="none"
            $xl-display="flex"
            task={selectedTask}
            tasks={tasks}
            readOnly
          />
        </HStack>
      </VStack>
    </ScrollableScreenContainer>
  );
};

export default ModulePreview;
